"use client";

/**
 * The new landing's header (vestra-v3 pages/landing.html, `<header class="nav">`), for the pages vestra.ai
 * serves from this app beside the landing: the legal pages. Founder, 2026-10-19: "in terms and conditions
 * privacy policy etc the header is wrong bro" (they wore the old site's). The landing's pages in its order,
 * with his same-day cut (no About us in the header). Plain anchors: "/" and its sections are the landing,
 * served from vestra-new, so they need a full page load.
 */
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { SOLUTIONS } from "./solutions";

const PAGES = [
  { label: "Brain", href: "/#brain" },
  { label: "Bash", href: "/#bash" },
  { label: "Multiplayer", href: "/#together" },
  { label: "Pricing", href: "/pricing" },
] as const;

const NAV_LINK =
  "rounded-full px-3.5 py-2 text-base font-bold opacity-90 transition-[opacity,background-color] duration-200 hover:bg-fd-site-hover hover:opacity-100";
const SHEET_LINK =
  "rounded-[14px] px-4 py-3.5 text-lg font-bold tracking-[-0.02em] hover:bg-fd-site-soft";

function Chevron({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={cn(
        "fill-none stroke-current [stroke-linecap:round] [stroke-linejoin:round] [stroke-width:2] transition-transform duration-300 ease-fd",
        className
      )}
    >
      <path d="m7 10 5 5 5-5" />
    </svg>
  );
}

export function SiteHeader() {
  const [stuck, setStuck] = useState(false);
  const [open, setOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const [solOpen, setSolOpen] = useState(false);
  const dd = useRef<HTMLDivElement>(null);

  // Clear glass once the page scrolls, as on the landing.
  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      setDdOpen(false);
    };
    const away = (e: MouseEvent) => {
      if (!dd.current?.contains(e.target as Node)) setDdOpen(false);
    };
    document.addEventListener("keydown", esc);
    document.addEventListener("mousedown", away);
    return () => {
      document.removeEventListener("keydown", esc);
      document.removeEventListener("mousedown", away);
    };
  }, []);

  return (
    <>
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-[60] font-fd-body text-fd-ink transition-[background-color,box-shadow] duration-300",
          stuck &&
            "bg-white/20 shadow-fd-site-stuck backdrop-blur-[18px] backdrop-saturate-[1.9]"
        )}
      >
        <div className="mx-auto flex h-18 max-w-[1440px] items-center gap-7 px-[clamp(20px,4vw,48px)]">
          <a
            href="/"
            aria-label="Vestra home"
            className="flex flex-none items-center gap-2.5"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/front-door/mark.svg" alt="" className="size-8" />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/front-door/wordmark.svg"
              alt=""
              className="h-5.5 w-auto"
            />
          </a>

          <nav
            aria-label="Primary"
            className="mx-auto hidden gap-0.5 min-[961px]:flex"
          >
            {PAGES.map((p) => (
              <a key={p.href} href={p.href} className={NAV_LINK}>
                {p.label}
              </a>
            ))}
            <div ref={dd} className="group/dd relative">
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={ddOpen}
                onClick={() => setDdOpen((o) => !o)}
                className="inline-flex cursor-pointer items-center gap-1 rounded-full py-2 pr-3 pl-3.5 text-base font-bold opacity-90 transition-[opacity,background-color] duration-200 group-hover/dd:bg-fd-site-hover group-hover/dd:opacity-100 aria-expanded:bg-fd-site-hover aria-expanded:opacity-100"
              >
                Solutions
                <Chevron
                  className={cn(
                    "size-3.75 group-hover/dd:rotate-180",
                    ddOpen && "rotate-180"
                  )}
                />
              </button>
              <div
                role="menu"
                aria-label="Solutions"
                className={cn(
                  "absolute top-[calc(100%+10px)] left-1/2 grid w-140 -translate-x-1/2 grid-cols-2 gap-1 rounded-[22px] bg-white p-2.5 shadow-fd-site-menu transition-[opacity,translate,visibility] duration-300 ease-fd before:absolute before:inset-x-0 before:-top-3 before:h-3 before:content-['']",
                  ddOpen
                    ? "visible translate-y-0 opacity-100"
                    : "invisible translate-y-1.5 opacity-0 group-focus-within/dd:visible group-focus-within/dd:translate-y-0 group-focus-within/dd:opacity-100 group-hover/dd:visible group-hover/dd:translate-y-0 group-hover/dd:opacity-100"
                )}
              >
                {SOLUTIONS.map((s) => (
                  <a
                    key={s.href}
                    role="menuitem"
                    href={s.href}
                    className="group/item flex items-start gap-3 rounded-[14px] p-3 hover:bg-fd-site-soft"
                  >
                    <span className="grid size-10.5 flex-none place-items-center rounded-xl bg-white text-fd-ink shadow-fd-site-tile transition-shadow duration-200 group-hover/item:shadow-fd-site-tile-hover">
                      <svg
                        viewBox="0 0 256 256"
                        aria-hidden="true"
                        className="size-5.5 fill-current"
                      >
                        <path d={s.icon} />
                      </svg>
                    </span>
                    <span>
                      <b className="block text-base font-bold tracking-[-0.01em]">
                        {s.title}
                      </b>
                      <small className="mt-0.5 block text-sm leading-[1.4] whitespace-normal text-fd-ink-3">
                        {s.line}
                      </small>
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </nav>

          <div className="flex items-center gap-2 max-[960px]:ml-auto">
            <a
              href="/login"
              className="inline-flex h-10 items-center rounded-full bg-fd-ink px-4.5 text-sm font-bold whitespace-nowrap text-white shadow-fd-site-btn transition-[transform,background-color] duration-200 hover:bg-fd-ink-hover active:scale-[0.97] max-[960px]:hidden"
            >
              Get Started
            </a>
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              className="relative size-11 cursor-pointer rounded-full bg-fd-site-hover min-[961px]:hidden"
            >
              <i
                className={cn(
                  "absolute inset-x-3.5 h-0.5 rounded-xs bg-fd-ink transition-[top,rotate] duration-400 ease-fd",
                  open ? "top-5.25 rotate-45" : "top-4.5"
                )}
              />
              <i
                className={cn(
                  "absolute inset-x-3.5 h-0.5 rounded-xs bg-fd-ink transition-[top,rotate] duration-400 ease-fd",
                  open ? "top-5.25 -rotate-45" : "top-6.25"
                )}
              />
            </button>
          </div>
        </div>
      </header>

      <div
        data-testid="site-header-sheet"
        className={cn(
          "fixed inset-x-3 top-20 z-[59] grid gap-1 rounded-3xl bg-white p-3.5 font-fd-body text-fd-ink shadow-fd-site-menu transition-[opacity,translate,scale] duration-300 ease-fd min-[961px]:hidden",
          open
            ? "translate-y-0 scale-100 opacity-100"
            : "pointer-events-none -translate-y-2 scale-[0.98] opacity-0"
        )}
      >
        {PAGES.map((p) => (
          <a key={p.href} href={p.href} className={SHEET_LINK}>
            {p.label}
          </a>
        ))}
        <button
          type="button"
          aria-expanded={solOpen}
          onClick={() => setSolOpen((o) => !o)}
          className={cn(
            SHEET_LINK,
            "flex w-full cursor-pointer items-center justify-between text-left"
          )}
        >
          Solutions
          <Chevron
            className={cn("size-4.5 opacity-60", solOpen && "rotate-180")}
          />
        </button>
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-350 ease-fd",
            solOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          )}
        >
          <div
            className={cn(
              "grid min-h-0 gap-1 overflow-hidden",
              solOpen ? "visible" : "invisible"
            )}
          >
            {SOLUTIONS.map((s) => (
              <a
                key={s.href}
                href={s.href}
                className="rounded-[14px] py-3.5 pr-4 pl-7 text-base font-bold tracking-[-0.02em] hover:bg-fd-site-soft"
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>
        <a
          href="/login"
          className="mt-2 inline-flex h-12 items-center justify-center rounded-full bg-fd-ink px-5 text-base font-bold text-white shadow-fd-site-btn"
        >
          Get Started
        </a>
      </div>
    </>
  );
}
